import React, { useState, useEffect, useRef, useContext } from "react";
import videojs from "video.js";
import { registerIVSQualityPlugin, registerIVSTech } from "amazon-ivs-player";
import { AudioAppContext } from "../../contexts/AudioContext"
import dynamic from "next/dynamic";

// const STREAM_TEST_URL =
//   "https://usher.ttvnw.net/api/lvs/hls/lvs.lvs-client-example.c6341be8-a3c7-42bc-b89a-8dabe040eae9.m3u8";

// const MP4_TEST_URL =
//   "http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

const VideoJSPlayer = ({ src = "" }) => {
  const videoRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [hidden, setHidden] = useState(true);
  const [reconnectTimeout, setReconnectTimeout] = useState(null);
  const playerID = "videojs-player";
  const RECONNECT_INTERVAL = 5000;
  const { mute, audioPlaybackAllowed } = useContext(AudioAppContext)

  useEffect(() => {
    initVideoJS(playerID);
    return () => {
      disposeVideoJS(playerID);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [videoRef]);

  useEffect(() => {
    if (player) {
      const IVSEvents = player.getIVSEvents().PlayerEventType;
      const IVSPlayer = player.getIVSPlayer();

      IVSPlayer.addEventListener(IVSEvents.ERROR, handleIVSErrors);
      player.on("error", handleVideoJSErrors);
      player.on("loadedmetadata", showPlayer);
      player.on("loadeddata", showPlayer);

      return () => {
        IVSPlayer.removeEventListener(IVSEvents.ERROR, handleIVSErrors);
        player.off("error", handleVideoJSErrors);
        player.off("loadedmetadata", showPlayer);
        player.off("loadeddata", showPlayer);
      };
    }
  }, [player]);

  useEffect(() => {
    if (player) {
      if (audioPlaybackAllowed) {
        player.muted(false)
      }
    }
  }, [mute, audioPlaybackAllowed, player])

  function initVideoJS(playerID) {
    if (videoRef?.current) {
      registerIVSTech(videojs, {
        wasmBinary: "/assets/script/amazon-ivs-wasmworker.min.wasm",
        wasmWorker: "/assets/script/amazon-ivs-wasmworker.min.js",
      });

      registerIVSQualityPlugin(videojs)

      const player = videojs(
        playerID,
        {
          techOrder: ["AmazonIVS"],
        },
        () => {
          player.enableIVSQualityPlugin();
          playVideo(player);
        }
      );

      setPlayer(player);
    }
  }

  function disposeVideoJS(playerID) {
    videojs(playerID).dispose();
  }

  function showPlayer() {
    setHidden(false);
  }

  function playVideo(player) {
    try {
      player.src(src);
      player.play();
      player.one("play", () => handleSetHighestQuality(player))
    } catch (error) {
      
    }
  }

  function handleSetHighestQuality(player) {
    const IVSPlayer = player.getIVSPlayer()
    const qualities = IVSPlayer.getQualities()
    if (qualities.length > 0) {
      const highest = qualities[0]
      IVSPlayer.setQuality(highest)
    }
  }

  function retryConnection() {
    const timeout = setTimeout(() => {
      playVideo(player);
    }, RECONNECT_INTERVAL);

    setReconnectTimeout(timeout);
  }

  function handleIVSErrors(error) {
    console.error(error);
    retryConnection();
    setHidden(true);
  }

  function handleVideoJSErrors(error) {
    console.error(error);
    retryConnection();
    setHidden(true);
  }

  return (
    <div
      className={`videojs-player-wrapper ${hidden ? "hidden": ""}`}
    >
      {hidden && (
      <p style={{
        position: 'absolute',
        fontSize: '12px',
        fontWeight: 'bold',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: '100',
      }}>
        Live Stream Offline
      </p>
      )}
      <div className="video-js vjs-theme-vvenues" data-vjs-player>
        <video
          controls
          autoPlay={true}
          ref={videoRef}
          id={playerID}
          playsInline
        ></video>
      </div>
    </div>
  );
};

export default dynamic(() => Promise.resolve(React.memo(VideoJSPlayer)), {
  ssr: false,
});
